import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Grid,
} from '@mui/material';
import katex from 'katex';
import 'katex/dist/katex.min.css';

import { CoordinateGrid } from '../coordinateGrid';
import {
  generateRationalGraphVariations,
  rationalToCurveElement,
  evaluateRationalAt,
  parseFraction,
  normalizeStep1Types,
  rationalEquationToLatex,
} from './rationalGraphUtils';
import { parseInputWithLabel } from '../shared/inputLabels';

// Parse a list of student input strings, returning index-aligned numeric and
// display arrays of only the valid entries.
const parseInputList = (inputs) => {
  const parsed = inputs.map((v) => parseInputWithLabel(v)).filter((p) => p.valid);
  return {
    values: parsed.map((p) => p.value),
    labels: parsed.map((p) => p.display),
  };
};

/**
 * Student component for answering rational graph questions
 *
 * Props:
 * - config: RationalGraphConfig
 * - value: StudentAnswer (current answer state)
 * - onChange: (answer) => void
 * - disabled: boolean (after submission)
 */
const RationalGraphStudent = ({
  config,
  value = {},
  onChange,
  disabled = false,
  showCorrectness = false,
}) => {
  const [step, setStep] = useState(value?.step2?.selectedGraphIndex !== undefined ? 2 : 1);
  const [intercepts, setIntercepts] = useState(value?.step1?.intercepts?.map(String) || []);
  const [yInterceptInput, setYInterceptInput] = useState(value?.step1?.yIntercept?.toString() || '');
  const [vaInputs, setVaInputs] = useState(value?.step1?.verticalAsymptotes?.map(String) || []);
  const [haInput, setHaInput] = useState(value?.step1?.horizontalAsymptote?.toString() || '');
  const [validationError, setValidationError] = useState('');
  const [selectedGraph, setSelectedGraph] = useState(value?.step2?.selectedGraphIndex ?? null);
  const [parseError, setParseError] = useState('');

  const { parsedRational, grid, interceptTolerance = 0.01 } = config || {};

  // Normalize step1Types for backward compatibility
  const step1Types = normalizeStep1Types(config || {});
  const step1TypesKey = step1Types.join(',');
  const hasX = step1Types.includes('x');
  const hasY = step1Types.includes('y');
  const hasVA = step1Types.includes('va');
  const hasHA = step1Types.includes('ha');

  // Generate variations (using stored seed for consistency)
  const variationsResult = useMemo(() => {
    if (!parsedRational) return { variations: [], error: '' };
    try {
      const { variations } = generateRationalGraphVariations(
        parsedRational,
        step1Types,
        config.variationSeed
      );
      return { variations, error: '' };
    } catch (e) {
      console.error('Failed to generate variations:', e);
      return { variations: [], error: 'Error loading question. Please contact your instructor.' };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedRational, step1TypesKey, config?.variationSeed]);

  const variations = variationsResult.variations;

  useEffect(() => {
    setParseError(variationsResult.error);
  }, [variationsResult.error]);

  // Expected counts. `?? 1` (not `|| 1`): a parsed rational with an empty list has
  // ZERO of that feature — demanding 1 input made the question unanswerable (e.g. a
  // quad-only denominator like 6/(x²+1) has no vertical asymptotes).
  const expectedXCount = parsedRational?.xIntercepts?.length ?? 1;
  const expectedVACount = parsedRational?.verticalAsymptotes?.length ?? 1;

  // Handle input changes
  const handleInterceptChange = useCallback((index, inputValue) => {
    const newIntercepts = [...intercepts];
    newIntercepts[index] = inputValue;
    setIntercepts(newIntercepts);
    setValidationError('');
  }, [intercepts]);

  const handleVAChange = useCallback((index, inputValue) => {
    const newVA = [...vaInputs];
    newVA[index] = inputValue;
    setVaInputs(newVA);
    setValidationError('');
  }, [vaInputs]);

  // Validate all step1 inputs and move to step 2
  const handleValidateIntercepts = useCallback(() => {
    const errors = [];
    const step1Data = { timestamp: new Date().toISOString() };

    // Validate X-intercepts
    if (hasX) {
      const { values: studentXValues, labels: studentXLabels } = parseInputList(intercepts);
      if (studentXValues.length !== expectedXCount) {
        errors.push(`Please enter ${expectedXCount} x-intercept value(s).`);
      } else {
        const correctRoots = (parsedRational.xIntercepts || [])
          .map(xi => xi.value).sort((a, b) => a - b);
        const sortedStudent = [...studentXValues].sort((a, b) => a - b);
        const xCorrect = correctRoots.length === sortedStudent.length &&
          correctRoots.every((r, i) => Math.abs(r - sortedStudent[i]) <= interceptTolerance);
        if (!xCorrect) {
          errors.push('X-intercept(s) incorrect.');
        } else {
          step1Data.intercepts = studentXValues;
          step1Data.interceptLabels = studentXLabels;
        }
      }
    }

    // Validate Y-intercept
    if (hasY) {
      const yParsed = parseInputWithLabel(yInterceptInput);
      if (!yParsed.valid) {
        errors.push('Please enter a valid y-intercept value.');
      } else {
        const studentYValue = yParsed.value;
        const correctYInt = evaluateRationalAt(parsedRational, 0);
        const yCorrect = !isNaN(correctYInt) && Math.abs(correctYInt - studentYValue) <= interceptTolerance;
        if (!yCorrect) {
          errors.push('Y-intercept incorrect.');
        } else {
          step1Data.yIntercept = studentYValue;
          step1Data.yInterceptLabel = yParsed.display;
        }
      }
    }

    // Validate Vertical Asymptotes
    if (hasVA) {
      const { values: studentVAValues, labels: studentVALabels } = parseInputList(vaInputs);
      if (studentVAValues.length !== expectedVACount) {
        errors.push(`Please enter ${expectedVACount} vertical asymptote value(s).`);
      } else {
        const correctVAs = (parsedRational.verticalAsymptotes || [])
          .map(va => va.value).sort((a, b) => a - b);
        const sortedStudent = [...studentVAValues].sort((a, b) => a - b);
        const vaCorrect = correctVAs.length === sortedStudent.length &&
          correctVAs.every((r, i) => Math.abs(r - sortedStudent[i]) <= interceptTolerance);
        if (!vaCorrect) {
          errors.push('Vertical asymptote(s) incorrect.');
        } else {
          step1Data.verticalAsymptotes = studentVAValues;
          step1Data.verticalAsymptoteLabels = studentVALabels;
        }
      }
    }

    // Validate Horizontal Asymptote
    if (hasHA) {
      const haParsed = parseInputWithLabel(haInput);
      if (!haParsed.valid) {
        errors.push('Please enter a valid horizontal asymptote value.');
      } else {
        const studentHAValue = haParsed.value;
        const correctHA = parsedRational.horizontalAsymptote?.value;
        const haCorrect = correctHA != null && Math.abs(correctHA - studentHAValue) <= interceptTolerance;
        if (!haCorrect) {
          errors.push('Horizontal asymptote incorrect.');
        } else {
          step1Data.horizontalAsymptote = studentHAValue;
          step1Data.horizontalAsymptoteLabel = haParsed.display;
        }
      }
    }

    if (errors.length > 0) {
      setValidationError(errors.join(' ') + ' Please check your calculations.');
      return;
    }

    setStep(2);
    setValidationError('');
    onChange({
      ...value,
      step1: step1Data,
    });
  }, [intercepts, yInterceptInput, vaInputs, haInput, expectedXCount, expectedVACount,
      hasX, hasY, hasVA, hasHA, parsedRational, interceptTolerance, value, onChange]);

  // Handle graph selection
  const handleGraphSelect = useCallback((index) => {
    if (disabled) return;

    setSelectedGraph(index);
    onChange({
      ...value,
      step1: value.step1 || { intercepts: intercepts.map(v => parseFraction(v)) },
      step2: {
        selectedGraphIndex: index,
        timestamp: new Date().toISOString(),
      },
    });
  }, [disabled, value, intercepts, onChange]);

  if (parseError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{parseError}</Alert>
      </Box>
    );
  }

  if (!config || !parsedRational) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Question configuration is missing.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Equation Display */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" align="center">
          <span dangerouslySetInnerHTML={{
            __html: katex.renderToString(rationalEquationToLatex(config.equation), { throwOnError: false })
          }} />
        </Typography>
      </Paper>

      {/* Step 1: Value Input */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Step 1: Enter the{' '}
          {(() => {
            const labels = {
              x: 'x-intercepts (zeros)',
              y: 'y-intercept',
              va: 'vertical asymptotes',
              ha: 'horizontal asymptote',
            };
            return step1Types.map(t => labels[t]).join(' and ');
          })()}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {(() => {
            const descs = [];
            if (hasX) descs.push('the zeros (where numerator = 0)');
            if (hasY) descs.push('the y-intercept (f(0))');
            if (hasVA) descs.push('the vertical asymptotes (where denominator = 0)');
            if (hasHA) descs.push('the horizontal asymptote');
            return `Find ${descs.join(' and ')}.`;
          })()}
        </Typography>

        {/* X-intercept inputs */}
        {hasX && (
          <>
            {step1Types.length > 1 && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                X-Intercepts (Zeros):
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              {Array.from({ length: expectedXCount }).map((_, index) => (
                <TextField
                  key={`x-${index}`}
                  label={`x-intercept ${index + 1}`}
                  value={intercepts[index] || ''}
                  onChange={(e) => handleInterceptChange(index, e.target.value)}
                  disabled={step === 2 || disabled}
                  placeholder="e.g., -3, 1/2, 2.5"
                  sx={{ width: 150 }}
                />
              ))}
            </Box>
          </>
        )}

        {/* Y-intercept input */}
        {hasY && (
          <>
            {step1Types.length > 1 && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Y-Intercept:
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="y-intercept"
                value={yInterceptInput}
                onChange={(e) => { setYInterceptInput(e.target.value); setValidationError(''); }}
                disabled={step === 2 || disabled}
                placeholder="e.g., -3, 1/2, 2.5"
                sx={{ width: 150 }}
              />
            </Box>
          </>
        )}

        {/* Vertical Asymptote inputs */}
        {hasVA && (
          <>
            {step1Types.length > 1 && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Vertical Asymptotes:
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              {Array.from({ length: expectedVACount }).map((_, index) => (
                <TextField
                  key={`va-${index}`}
                  label={`vertical asymptote ${index + 1}`}
                  value={vaInputs[index] || ''}
                  onChange={(e) => handleVAChange(index, e.target.value)}
                  disabled={step === 2 || disabled}
                  placeholder="e.g., -1, 4"
                  sx={{ width: 150 }}
                />
              ))}
            </Box>
          </>
        )}

        {/* Horizontal Asymptote input */}
        {hasHA && (
          <>
            {step1Types.length > 1 && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Horizontal Asymptote:
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="horizontal asymptote"
                value={haInput}
                onChange={(e) => { setHaInput(e.target.value); setValidationError(''); }}
                disabled={step === 2 || disabled}
                placeholder="e.g., 0, 1, 1/2"
                sx={{ width: 150 }}
              />
            </Box>
          </>
        )}

        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        {step === 1 && !disabled && (
          <Button
            variant="contained"
            onClick={handleValidateIntercepts}
            disabled={(() => {
              if (hasX && intercepts.filter(v => v?.trim?.()).length < expectedXCount) return true;
              if (hasY && !yInterceptInput?.trim?.()) return true;
              if (hasVA && vaInputs.filter(v => v?.trim?.()).length < expectedVACount) return true;
              if (hasHA && !haInput?.trim?.()) return true;
              return false;
            })()}
          >
            Check & Continue
          </Button>
        )}

        {step === 2 && (
          <Alert severity="success">
            Correct! Now select the graph that matches this rational function.
          </Alert>
        )}
      </Paper>

      {/* Step 2: Graph Selection */}
      {step === 2 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Step 2: Select the correct graph
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All graphs share the value(s) you found.
            Choose the one that correctly represents the given rational function.
          </Typography>

          <Grid container spacing={2}>
            {variations.map((variation, index) => {
              const isSelected = selectedGraph === index;
              const showResult = showCorrectness && isSelected;
              const isCorrectChoice = showResult && variation.isCorrect;
              const isWrongChoice = showResult && !variation.isCorrect;
              const showCorrectHint = showCorrectness && selectedGraph !== null && !isSelected && variation.isCorrect;

              let borderStyle = '1px solid grey';
              if (isCorrectChoice || showCorrectHint) borderStyle = '3px solid green';
              else if (isWrongChoice) borderStyle = '3px solid red';
              else if (isSelected) borderStyle = '3px solid blue';

              let bgColor = 'background.paper';
              if (isCorrectChoice || showCorrectHint) bgColor = 'success.light';
              else if (isWrongChoice) bgColor = 'error.light';

              return (
                <Grid item xs={6} key={variation.label}>
                  <Paper
                    onClick={() => handleGraphSelect(index)}
                    sx={{
                      p: 1,
                      cursor: disabled ? 'default' : 'pointer',
                      border: borderStyle,
                      bgcolor: bgColor,
                      '&:hover': disabled ? {} : {
                        borderColor: 'primary.main',
                        bgcolor: showResult ? undefined : 'action.hover',
                      },
                    }}
                  >
                    <Typography variant="subtitle2" align="center">
                      Graph {variation.label}
                      {isCorrectChoice && ' (Correct!)'}
                      {isWrongChoice && ' (Incorrect)'}
                      {showCorrectHint && ' (Correct)'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <CoordinateGrid
                        config={grid}
                        elements={[rationalToCurveElement(variation.rational, grid)]}
                        pendingPoints={[]}
                        mode="point"
                        readOnly={true}
                        width={180}
                        height={180}
                      />
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default RationalGraphStudent;
